import { cn } from "@qw/ui";
import { type FC, useEffect, useMemo, useState } from "react";

export interface AudioWaveformProps {
  levels: number[]; // 0-1 values for each bar height
  numBars?: number;
  minHeight?: number;
  maxHeight?: number;
  shimmerSpeed?: number;
  shimmerWidth?: number;
  shimmerIntensity?: number;
  className?: string;
  barClassName?: string;
}

const DEFAULT_MIN_HEIGHT = 3; // Minimum bar height in pixels
const DEFAULT_MAX_HEIGHT = 18; // Maximum bar height in pixels
const DEFAULT_NUM_BARS = 8;

// Shimmer effect configuration
const DEFAULT_SHIMMER_SPEED = 1.5; // How fast the shimmer traverses (cycles per second)
const DEFAULT_SHIMMER_WIDTH = 2; // How many bars the shimmer affects at once
const DEFAULT_SHIMMER_INTENSITY = 0.6; // How much brighter the shimmer makes bars (0-1)

// Deterministic pseudo-random based on seed. We use this instead of Math.random()
// because React requires render to be pure—Math.random() would violate idempotency.
const seededRandom = (seed: number): number => {
  const x = Math.sin(seed * 12_345) * 10_000;
  return x - Math.floor(x);
};

/**
 * Calculate shimmer brightness for a bar based on its distance from the shimmer position.
 * The shimmer travels from bar 0 to bar 7 and back in a continuous cycle.
 */
function getShimmerBrightness(options: {
  barIndex: number;
  time: number;
  numBars: number;
  shimmerSpeed: number;
  shimmerWidth: number;
  shimmerIntensity: number;
}): number {
  const { barIndex, time, numBars, shimmerSpeed, shimmerWidth, shimmerIntensity } = options;

  // Create a ping-pong motion: 0 → NUM_BARS-1 → 0
  const cyclePosition = (time * shimmerSpeed) % 2; // 0 to 2
  const shimmerPosition = cyclePosition <= 1 ? cyclePosition * (numBars - 1) : (2 - cyclePosition) * (numBars - 1);

  // Calculate distance from shimmer center
  const distance = Math.abs(barIndex - shimmerPosition);

  // Smooth falloff based on distance from shimmer center
  if (distance < shimmerWidth) {
    return shimmerIntensity * (1 - distance / shimmerWidth);
  }
  return 0;
}

export const AudioWaveform: FC<AudioWaveformProps> = ({
  levels,
  numBars = DEFAULT_NUM_BARS,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
  shimmerSpeed = DEFAULT_SHIMMER_SPEED,
  shimmerWidth = DEFAULT_SHIMMER_WIDTH,
  shimmerIntensity = DEFAULT_SHIMMER_INTENSITY,
  className,
  barClassName,
}) => {
  const [time, setTime] = useState(0);

  // Animate the waveform
  useEffect(() => {
    let animationId: number;
    let lastTime = performance.now();

    const animate = (currentTime: number) => {
      const delta = (currentTime - lastTime) / 1000;
      lastTime = currentTime;
      setTime((t) => t + delta);
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Each bar has its own oscillation speed and phase offset
  const BAR_CONFIGS = useMemo(
    () =>
      Array.from({ length: numBars }, (_, i) => ({
        speedMultiplier: 0.8 + seededRandom(i * 2) * 0.6, // 0.8-1.4x speed variation
        phaseOffset: (i / numBars) * Math.PI * 2 + seededRandom(i * 2 + 1) * 0.5, // Spread phases + randomness
      })),
    [numBars]
  );

  return (
    <div className={cn("flex items-center gap-0.5 h-5", className)}>
      {BAR_CONFIGS.map((config, index) => {
        // Each bar uses its own frequency band level
        const barFreqLevel = levels[index] ?? 0;

        // Create gentle oscillating wave pattern - each bar dances at its own rhythm
        const oscillation = Math.sin(time * 8 * config.speedMultiplier + config.phaseOffset);
        const oscillation2 = Math.sin(time * 5 * config.speedMultiplier + config.phaseOffset * 1.7);
        const wave = (oscillation * 0.6 + oscillation2 * 0.4 + 1) / 2; // 0 to 1

        // Combine frequency level with gentle oscillation for subtle dancing effect
        // The oscillation only slightly modulates the frequency level
        const barLevel = barFreqLevel * (0.7 + wave * 0.3);

        const height = minHeight + Math.min(1, barLevel) * (maxHeight - minHeight);

        // Calculate shimmer effect - a traveling highlight across the bars
        const shimmerBrightness = getShimmerBrightness({
          barIndex: index,
          time,
          numBars,
          shimmerSpeed,
          shimmerWidth,
          shimmerIntensity,
        });
        const opacity = 0.4 + shimmerBrightness + barLevel * 0.3; // Base opacity + shimmer + audio level

        return (
          <div
            key={index}
            style={{
              height: `${height}px`,
              opacity: Math.min(1, opacity),
            }}
            className={cn("w-0.5 bg-primary rounded-full transition-opacity duration-75", barClassName)}
          />
        );
      })}
    </div>
  );
};
